import { useState, useEffect } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@clerk/react";
import type { UserFavorite, UserRoom } from "@tuit/shared-types";
import { fetchFavorites, fetchUserRooms } from "../api.js";
import { ViewSettings } from "./ViewSettings.js";
import { AboutPanel } from "./AboutPanel.js";
import { GROUPS } from "../groups.js";
import styles from "../styles/components/BottomBar.module.css";
import { LuHouse, LuNewspaper, LuSettings } from "react-icons/lu";
import { FaHouse, FaNewspaper, FaGear, FaStar } from "react-icons/fa6";

type Panel = "rooms" | "settings";

export function BottomBar() {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const [activePanel, setActivePanel] = useState<Panel | null>(null);
  const [userRooms, setUserRooms] = useState<UserRoom[]>([]);
  const [favorites, setFavorites] = useState<UserFavorite[]>([]);

  const isHome = activePanel === null && location.pathname === "/";

  useEffect(() => {
    if (!isSignedIn) {
      setUserRooms([]);
      setFavorites([]);
      return;
    }
    fetchUserRooms().then(setUserRooms).catch(() => {});
    fetchFavorites().then(setFavorites).catch(() => {});
  }, [isSignedIn]);

  useEffect(() => {
    function handler() { setActivePanel("rooms"); }
    window.addEventListener("open-rooms-panel", handler);
    return () => window.removeEventListener("open-rooms-panel", handler);
  }, []);

  function toggle(panel: Panel) {
    setActivePanel((prev) => (prev === panel ? null : panel));
  }

  function goHome() {
    setActivePanel(null);
    navigate({ to: "/" });
  }

  const favoritedGroupIds = new Set(
    favorites.filter((f) => f.roomType === "system").map((f) => f.roomId),
  );
  const favoritedUserRoomIds = new Set(
    favorites.filter((f) => f.roomType === "user").map((f) => f.roomId),
  );

  return (
    <>
      {activePanel && (
        <div className={styles.backdrop} data-settings={activePanel !== null ? "" : undefined} onClick={() => setActivePanel(null)}>
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <div className={styles.sheetHandle} />
            {activePanel === "rooms" && (
              <div className={styles.roomList}>
                {favoritedGroupIds.size > 0 && (
                  <>
                    <div className={styles.roomSectionLabel}>Favorites</div>
                    {GROUPS.filter((g) => favoritedGroupIds.has(g.id)).map((g) => (
                      <Link
                        key={g.id}
                        to="/$roomSlug"
                        params={{ roomSlug: `~${g.id}` }}
                        className={styles.roomLink}
                        onClick={() => setActivePanel(null)}
                      >
                        <FaStar className={styles.roomFavStar} />
                        {g.title}
                      </Link>
                    ))}
                    {userRooms
                      .filter((r) => favoritedUserRoomIds.has(r.id))
                      .map((room) => (
                        <Link
                          key={room.id}
                          to="/$roomSlug"
                          params={{ roomSlug: `~${room.id}` }}
                          className={styles.roomLink}
                          onClick={() => setActivePanel(null)}
                        >
                          <FaStar className={styles.roomFavStar} />
                          {room.title}
                        </Link>
                      ))}
                  </>
                )}
                <div className={styles.roomSectionLabel}>All Rooms</div>
                {GROUPS.filter((g) => !favoritedGroupIds.has(g.id)).map((g) => (
                  <Link
                    key={g.id}
                    to="/$roomSlug"
                    params={{ roomSlug: `~${g.id}` }}
                    className={styles.roomLink}
                    onClick={() => setActivePanel(null)}
                  >
                    {g.title}
                  </Link>
                ))}
                {isSignedIn && userRooms.filter((r) => !favoritedUserRoomIds.has(r.id)).length > 0 && (
                  <>
                    <div className={styles.roomSectionLabel}>My Rooms</div>
                    {userRooms.filter((r) => !favoritedUserRoomIds.has(r.id)).map((room) => (
                      <Link
                        key={room.id}
                        to="/$roomSlug"
                        params={{ roomSlug: `~${room.id}` }}
                        className={styles.roomLink}
                        onClick={() => setActivePanel(null)}
                      >
                        {room.title}
                      </Link>
                    ))}
                  </>
                )}
              </div>
            )}
            {activePanel === "settings" && (
              <>
                <ViewSettings mobile />
                <div className={styles.aboutDivider} />
                <AboutPanel />
              </>
            )}
          </div>
        </div>
      )}
      <nav className={styles.bar}>
        <button
          type="button"
          className={`${styles.btn} ${isHome ? styles.btnActive : ""}`}
          onClick={goHome}
        >
          {isHome ? <FaHouse size={18} /> : <LuHouse size={18} />}
          Home
        </button>
        <button
          type="button"
          className={`${styles.btn} ${activePanel === "rooms" ? styles.btnActive : ""}`}
          onClick={() => toggle("rooms")}
        >
          {activePanel === "rooms" ? <FaNewspaper size={18} /> : <LuNewspaper size={18} />}
          Rooms
        </button>
        <button
          type="button"
          className={`${styles.btn} ${activePanel === "settings" ? styles.btnActive : ""}`}
          onClick={() => toggle("settings")}
        >
          {activePanel === "settings" ? <FaGear size={18} /> : <LuSettings size={18} />}
          Settings
        </button>
      </nav>
    </>
  );
}
